import { type } from "arktype";
/**
 * Auto-captured app/device context attached to a bug/feature report (`Feedback.metadata_raw`).
 * Every field is best-effort and optional — the mobile client fills in whatever it can read at
 * submit time. Used by the admin triage page to reproduce bugs without round-tripping the user.
 */
export const feedbackMetadataSchema = type({
    "appVersion?": "string",
    "buildNumber?": "string",
    "platform?": "'ios' | 'android'",
    "osVersion?": "string",
    "deviceModel?": "string",
    "locale?": "string",
    // Deploy git hash of the JS bundle the user was running when they submitted. Matches the
    // `git_hash` field on mobile error logs, so a report and any concurrent ERROR logs from the
    // same user can be cross-referenced to a single commit.
    "deployGitHash?": "string",
    // Current in-app route when the report was opened (most useful for bugs).
    "route?": "string",
    // Last-5 route breadcrumb (newest first, full URLs with query params) — same ring buffer fed
    // by `subscribeToFocusedUrl` and attached to mobile ERROR logs. Index 0 is what the user was
    // viewing when they opened/submitted the report; later entries reveal the path that led there.
    // The mobile sender compacts undefined slots out before submit, so the array length tells you
    // how many distinct routes have actually been visited (≤ 5), not how many ring-buffer slots
    // happen to exist.
    "routeHistory?": "string[]",
    // Position within a FlowRunner flow when the report was opened. A flow (an onboarding step,
    // tutorial, education lesson, addBank, …) runs behind a single `route`, advancing its pages
    // internally — so `route` alone can't say which page. Present only when a flow was on screen.
    // Keys were `stepId/stepIndex/stepCount/subStep` before 2026-08-19 (ADR: flow-terminology);
    // existing rows were migrated in place (roo-db `rename_flow_location_keys`).
    "flowLocation?": {
        // `PageDef.lid` of the visible page (an opaque `r-<8alpha>` lid).
        pageLid: "string",
        // 0-based position of the visible page among all pages in the flow.
        pageIndex: "number",
        // Total number of pages in the flow (including skippable ones).
        pageCount: "number",
        // Current segment within the page, when the page reports them. Omitted at segment 0.
        "segment?": "number",
        // Host-supplied flow name (e.g. "onboarding-step-1") for legible triage. Required:
        // every FlowRunner names its flow, so a present flowLocation always carries one.
        flowId: "string",
    },
    // Structured context for an amount-override "report this to our team" submission (mobile sign-flip
    // flow — decision 7). Filed silently (no modal/navigation) right after the user flips a transaction's
    // sign, so support gets machine-readable txn context instead of free text. Optional + additive ⇒
    // jsonb-expand-safe (old rows still satisfy the schema). Plain object (no `.or()`) so it's safe under
    // the mobile arktype Hermes stub — though validation is server-only regardless.
    "amountOverrideReport?": {
        // The transaction the override was applied to.
        transactionId: "string",
        // Bank-reported raw `c_amount` at report time (what we believe is wrong).
        rawAmount: "number",
        // The user-applied effective amount (sign-flipped: `-rawAmount`).
        overrideAmount: "number",
        "accountId?": "string",
        "institutionName?": "string",
    },
    // Set by the server on the review pulse's follow-up answer: the Net Promoter score (0–10) the user gave
    // just before typing this, and its category, so triage reads "detractor, 3" next to the words. Never set
    // by the client. Optional + additive, so old rows still satisfy the schema.
    "reviewPulse?": {
        score: "number",
        category: "'detractor' | 'passive' | 'promoter'",
        // The `shownAt` (epoch ms) of the pulse this comment answers — with `user_id`, the exact key that joins
        // a comment to its pulse on the admin Pulse responses page. Absent when the ledger refused the answer
        // (no pulse was closed, so there is nothing to name). Rows from before this field were backfilled by
        // the `review_pulse_follow_up` migration; one it couldn't match stays without it.
        "shownAt?": "number",
    },
});
/**
 * Who authored a feedback report (`feedback.created_by_kind`): "user" = the mobile submit flow;
 * "admin" = logged on the user's behalf via the admin portal (born submitted, no draft phase, no
 * Slack notification); "bot" = filed from a support conversation by the in-chat assistant's
 * file_feedback tool. NB there is deliberately NO "agent" kind: autonomous coding/workflow agents
 * (agent:claude@kevin) create ISSUES, never feedback — they have no user to file on behalf of, and
 * feedback-intake isn't on the agent MCP surface (ADR: agent-access-planes). On-behalf creation is an admin action.
 */
export const FEEDBACK_CREATED_BY_KINDS = ["user", "admin", "bot"];
//# sourceMappingURL=feedback.models.js.map